import { AfterViewInit, Directive, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { SessionStorage } from '@df/browser/storage/decorator/session-storage';
import { CheckoutAccountComponentDef } from '@df/checkout/checkout-account.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { DfEventService } from '@df/core/service/df-event';
import { MultisiteService } from '@df/core/service/multisite';
import { Status } from '@df/core/status';
import { NDfModuleLoqate } from '@df/module-loqate/df-module-loqate.config';
import { SagepayCloudPaymentService } from '@df/module-sagepay';
import { OnChange } from '@df/ng/on-change';
import { injectTimeoutService } from '@df/ng/timeout.service';
import { Login } from '@df/session/login/login.interfaces';
import { ExponeaService } from '../../module/exponea/exponea.service';
import { CheckoutFormComponent } from '../checkout-form.component';

declare module '@df/session/login/login.interfaces' {
    export namespace Login {
        export interface ILoginFormData {
            newsletter?: boolean;
        }
    }
}

@Directive()
export abstract class CheckoutAccountAbstract extends CheckoutAccountComponentDef implements OnInit, OnChanges, AfterViewInit {
    @Input()
    @OnChange(function (value: boolean) {
        if (this.showSubscriptionOptInChange) {
            this.showSubscriptionOptInChange.emit(value);
        }
    })
    set showSubscriptionOptIn(value: boolean) {
        this._showSubscriptionOptIn = value;
    }

    get showSubscriptionOptIn(): boolean {
        return this._showSubscriptionOptIn;
    }

    /**
     * Is account required flag
     * If true then we will consider step as invalid till customer is logged in
     *
     * We will not allow guest checkout either
     */
    @Input()
    accountRequried = false;

    private _showSubscriptionOptIn = false;
    private _emailValidationIsComplete = false;

    @Output()
    showSubscriptionOptInChange = new EventEmitter<boolean>();

    @SessionStorage('exponeaConsent')
    exponeaConsentResult?: boolean;

    exponeaConsentStatus = new Status();

    private consentsSentFor: string[] = [];

    logoutSuccess = false;
    guestCheckoutIsActive = false;
    emailBlur = false;

    protected dfEventService = inject(DfEventService);
    protected spcpService = inject(SagepayCloudPaymentService);
    protected multisiteService = inject(MultisiteService);
    protected exponeaService = inject(ExponeaService);
    protected readonly disposableTimeoutService = injectTimeoutService();

    /**
     * Detect changes on inputs chance and set CMS Data
     */
    override ngOnInit() {
        super.ngOnInit();

        // check current email
        this.onEmailChange(this.data.email, this.data.email);

        this.subscriptions.add(this.form?.statusChanges?.subscribe(() => this.detectChanges()));

        this.subscriptions.add(
            this.app.events.on(Checkout.EVENTS.StepChange).subscribe((event: Checkout.IStepChangeEventData) => {
                if (this.showSubscriptionOptIn && event.stepBeforeChange === this.checkoutStepComponent.name) {
                    const email = this.customer.model.data?.email || this.email;
                    const customerProperties = {
                        first_name: this.shippingAddressData.firstname,
                        last_name: this.shippingAddressData.lastname,
                        email
                    };

                    if (email && this.consentsSentFor.indexOf(email) === -1) {
                        this.exponeaService.identifyCustomer(email, undefined, undefined, customerProperties);

                        if (!this.newsletter) {
                            this.consentsSentFor.push(email);
                            this.exponeaService.onNewsletterConsentChange(this.newsletter, email, 'checkout_start_softoptin');
                        }
                    }
                }

                if (this.logoutSuccess) {
                    this.logoutSuccess = false;

                    this.detectChanges();
                }
            })
        );

        this.subscriptions.add(
            this.app.events.on(Checkout.EVENTS.LogoutSuccess).subscribe(() => {
                this.onLogoutSuccess();

                this.logoutSuccess = true;

                this.detectChanges();
            })
        );

        this.subscribeToEmailValidation();
    }

    override ngAfterViewInit(): void {
        if (this.form?.statusChanges) {
            this.subscriptions.add(this.form.statusChanges.subscribe(() => this.detectChanges()));
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes) {
            this.newsletter = !!this.checkoutFormComponent?.data?.newsletter;
            this.ref.detectChanges();
        }
    }

    get newsletter(): boolean {
        return !!this.checkoutFormComponent?.data?.newsletter;
    }

    set newsletter(value: boolean) {
        (<Partial<Login.ILoginFormData>>this.data).newsletter = value;

        if (this.checkoutFormComponent?.data) {
            this.checkoutFormComponent.data.newsletter = value;
        }
    }

    get shouldFillRegisterData(): boolean {
        return this.isNewUser() || (this.guestCheckoutIsActive && !this.hasAccount());
    }

    override shouldRegister(): boolean {
        return this.isNewUser() && !this.spcpService.isSpcp();
    }

    override shouldLogin(): boolean {
        return super.shouldLogin() && !this.spcpService.isSpcp();
    }

    override canUseGuestCheckout() {
        return true;
    }

    /**
     * Checks if all valid
     */
    override get isValid(): boolean {
        if (this.customer.model.isLoggedIn) {
            return true;
        }

        if (this.checkStatus.busy) {
            if (this.email && this.checkoutFormComponent.isGuestCheckout && !this.accountRequried) {
                return true;
            }

            return false;
        }

        if (this.accountRequried) {
            return false;
        }

        if (!this.checkResult) {
            return false;
        }
        /// ---> CHANGE IS HERE ///
        if (this.hasAccount() && !this.isGuestCheckout && !this.spcpService.isSpcp()) {
            return false;
        }

        return !!this.form?.controls?.['email']?.valid;
    }

    handleKeyDown(event: KeyboardEvent): void {
        this.emailBlur = false;

        this.form['submitAttempt'] = false;
        this.form.ngSubmit.emit();

        if (event.key === 'Enter') {
            this.continue();
        }

        this.detectChanges();
    }

    handleEmailBlur(): void {
        this.emailBlur = true;

        this.detectChanges();
    }

    canShowEmailValidate(value: string): boolean {
        return (
            ((value?.length > 0 || (value?.length > 0 && this.shouldFillRegisterData)) && (this.form?.submitAttempt || this.emailBlur)) ||
            (value?.length == 0 && (this.form?.submitAttempt || this.emailBlur))
        );
    }

    continue(): void {
        this.emailBlur = false;

        if (this.isValid) {
            this.checkoutFormComponent.goToNextStep();
        } else {
            this.form['submitAttempt'] = true;
            this.form.ngSubmit.emit();
        }
    }

    continueAsGuestToDelivery(force = false) {
        if (force) {
            this.setGuestCheckout();

            this.guestCheckoutIsActive = true;
            this.form['submitAttempt'] = false;

            this.form.ngSubmit.emit();

            this.detectChanges();
        } else {
            if (this.form.valid) {
                this.setGuestCheckout();
                this.checkoutFormComponent.goToNextStep();
            } else {
                this.form['submitAttempt'] = true;
                this.form.ngSubmit.emit();
            }
        }
    }

    override leaveGuestCheckout(): void {
        this.guestCheckoutIsActive = false;

        super.leaveGuestCheckout();
    }

    get shippingAddressData(): Checkout.IAddress {
        return this.checkoutFormComponent.data.address.shipping;
    }

    get deliveryStepValid(): boolean {
        return (this.checkoutFormComponent as CheckoutFormComponent).deliveryStepValid;
    }

    onLoginSuccess = () => {
        this.disposableTimeoutService.setTimeout(() => {
            this.checkoutFormComponent.goToNextStep();
        }, 100);
    };

    protected override onLogoutSuccess(): void {
        if (this.data) {
            this.data.password = undefined;
        }

        this.email = undefined;

        this.logoutSuccess = true;

        this.detectChanges();
    }

    override onEmailChange(newEmail: string | undefined, oldEmail: string | undefined) {
        this.showSubscriptionOptInChange.emit(false);

        // reset on email change
        this.exponeaConsentResult = undefined;
        this._emailValidationIsComplete = false;

        if (newEmail) {
            this.exponeaService.checkConsent(this.exponeaConsentStatus, newEmail, result => {
                this.exponeaConsentResult = result.consent;

                this.showSubscriptionOptIn = !this.exponeaConsentResult;

                this.detectChanges();
            });
        }

        super.onEmailChange(newEmail, oldEmail);
    }

    dataChangeHandle(newsletter: boolean): void {
        this.newsletter = newsletter;

        this.detectChanges();
    }

    protected override setCheckResult(checkResult?: Login.IResponseAccountExists) {
        if (this.logoutSuccess && checkResult) {
            this.logoutSuccess = false;
        }

        this.leaveGuestCheckout();

        super.setCheckResult(checkResult);
    }

    protected subscribeToEmailValidation(): void {
        this.dfEventService.success(event => {
            if (event.id === NDfModuleLoqate.Events.EmailValidation) {
                this.app.logger.log('subscribeToEmailValidation');
                this._emailValidationIsComplete = true;
            }
        });
    }
}
